// 🔧 manual_vote.js
// ✅ Purpose: Public-facing manual voting browser for users who do not scan QR codes
// ✅ Features:
// - Allows voters to browse event categories and entries without scanning a QR code
// - Reuses existing `/vote/:carId` page for the actual vote submission
// - Keeps QR voting and manual voting on the same voting logic path
// - Loads categories from `/events/{eventId}/categories`
// - Loads entries from `/events/{eventId}/cars`
// - Optionally loads the current user's votes to show Vote / Update Vote messaging
// - Groups entries by category
// - Supports search by entry number, owner, year, model, or description
// - Shows success/error/info messages at the top of the page for 20 seconds
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { getEventVotingType } from "../../utils/event_voting_type_helpers";
import {
  isJudgedVotingFamily,
  isSelectionVotingFamily,
} from "../../utils/voting_engine_registry";
import { logger } from "../../utils/logger";
import {
  setSessionValue,
  removeSessionValue,
} from "../../utils/browser_storage_helpers";
import SeoHead from "../../components/seo_head";
import { buildEventPageSeo } from "../../utils/event_page_seo_helpers";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";

function ManualVote() {

  const navigate = useNavigate(); 

    // =====================================================
    // 🔗 Event Context
    // =====================================================
    // Standard LGV pattern:
    // - useEventContext resolves the event from the URL
    // - eventId is the Firestore event document id
    // - eventDoc contains event fields, including slug
    const { eventId, eventDoc, eventLoading, ready } = useEventContext({
    redirectIfNotFound: true,
    });

    // Use the event slug from the resolved event document.
    // This prevents undefined slugs when navigating from this page.
    const eventslug = eventDoc?.slug;

    const votingType = getEventVotingType(eventDoc);
    const isJudgedVoting = isJudgedVotingFamily(votingType);
    const isSelectionVoting = isSelectionVotingFamily(votingType);

    // =====================================================
    // 🧠 Local State
    // =====================================================
    const [categories, setCategories] = useState([]);
    const [entries, setEntries] = useState([]);
    const [userVotes, setUserVotes] = useState([]);
    const [selectedCatId, setSelectedCatId] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [loadingPage, setLoadingPage] = useState(true);
    const [status, setStatus] = useState(null);

    // =====================================================
    // 🔔 Status Helper
    // =====================================================
    const showPageStatus = (type, message) => {
        setStatus({ type, message });

        setTimeout(() => {
        setStatus(null);
        }, 20000);
    };

    // =====================================================
    // 📥 Load Manual Voting Data
    // =====================================================
    useEffect(() => {
        const loadManualVotingData = async () => {
        if (!ready || !eventId) return;

        trackEvent({
            action: GA_EVENTS.MANUAL_VOTE_VIEWED,
            parameters: {
                event_slug: eventslug,
                event_id: eventId,
                voting_type: votingType,
                source_page: window.location.pathname,
            },
        });

        try {
            setLoadingPage(true);

            // Load event categories.
            const catSnap = await getDocs(
            collection(db, "events", eventId, "categories")
            );

            const categoryData = catSnap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
            }));

            categoryData.sort((a, b) => {
            const sortA = Number.isFinite(Number(a?.sortOrder))
                ? Number(a.sortOrder)
                : Number.isFinite(Number(a?.id))
                ? Number(a.id)
                : 9999;

            const sortB = Number.isFinite(Number(b?.sortOrder))
                ? Number(b.sortOrder)
                : Number.isFinite(Number(b?.id))
                ? Number(b.id)
                : 9999;

            if (sortA !== sortB) {
                return sortA - sortB;
            }

            return String(a?.name ?? "").localeCompare(
                String(b?.name ?? ""),
                undefined,
                { sensitivity: "base" }
            );
            });

            // Load event entries/items.
            const entrySnap = await getDocs(
            collection(db, "events", eventId, "cars")
            );

            const entryData = entrySnap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
            }));

            entryData.sort((a, b) => {
            const aReg = Number(a.regcnt || 0);
            const bReg = Number(b.regcnt || 0);
            return aReg - bReg;
            });

            // Load current user's votes when logged in.
            // This is only used for display text. Actual voting still happens in vote.js.
            let voteData = [];

            if (auth.currentUser?.uid) {
            const voteQuery = query(
                collection(db, "events", eventId, "votes"),
                where("uid", "==", auth.currentUser.uid)
            );

            const voteSnap = await getDocs(voteQuery);

            voteData = voteSnap.docs.map((docSnap) => ({
                id: docSnap.id,
                ...docSnap.data(),
            }));
            }

            setCategories(categoryData);
            setEntries(entryData);
            setUserVotes(voteData);

        } catch (error) {
            logger.error(
            "❌ Failed to load manual voting page:",
            {
                value: error,
            }
            );
            showPageStatus(
            "error",
            "Unable to load manual voting options. Please refresh and try again."
            );
        } finally {
            setLoadingPage(false);
        }
        };

        loadManualVotingData();
    }, [ready, eventId]);

    // =====================================================
    // 🧮 Category Lookup
    // =====================================================
    const categoryNameById = useMemo(() => {
        const lookup = {};

        categories.forEach((cat) => {
        lookup[cat.id] = cat.name || cat.category || "Unnamed Category";
        });

        return lookup;
    }, [categories]);

    // =====================================================
    // 🧮 Filtered Entries
    // =====================================================
    const filteredEntries = useMemo(() => {
        let result = [...entries];

        if (selectedCatId) {
        result = result.filter((entry) => String(entry.catid) === String(selectedCatId));
        }

        if (searchTerm.trim()) {
        const lowered = searchTerm.toLowerCase();

        result = result.filter((entry) => {
            const searchableText = [
            entry.regcnt,
            entry.owner,
            entry.ownerFirst,
            entry.ownerLast,
            entry.year,
            entry.model,
            entry.description,
            categoryNameById[entry.catid],
            ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

            return searchableText.includes(lowered);
        });
        }

        return result;
    }, [entries, selectedCatId, searchTerm, categoryNameById]);

    // =====================================================
    // 🧮 Entries Grouped By Category
    // =====================================================
    // Groups the filtered entry list by catid so the manual voting page
    // displays clean category sections instead of one long flat list.
    const entriesGroupedByCategory = useMemo(() => {
    const grouped = {};

    filteredEntries.forEach((entry) => {
        const catid = String(entry.catid || "uncategorized");

        if (!grouped[catid]) {
        grouped[catid] = [];
        }

        grouped[catid].push(entry);
    });

    return Object.keys(grouped)
        .sort((a, b) => {
            const categoryA = categories.find(
            (category) => String(category.id) === String(a)
            );

            const categoryB = categories.find(
            (category) => String(category.id) === String(b)
            );

            const sortA = Number.isFinite(Number(categoryA?.sortOrder))
            ? Number(categoryA.sortOrder)
            : Number.isFinite(Number(categoryA?.id))
            ? Number(categoryA.id)
            : 9999;

            const sortB = Number.isFinite(Number(categoryB?.sortOrder))
            ? Number(categoryB.sortOrder)
            : Number.isFinite(Number(categoryB?.id))
            ? Number(categoryB.id)
            : 9999;

            if (sortA !== sortB) {
            return sortA - sortB;
            }

            return String(
            categoryNameById[a] || "Uncategorized"
            ).localeCompare(
            String(categoryNameById[b] || "Uncategorized"),
            undefined,
            { sensitivity: "base" }
            );
        })
        .map((catid) => ({
        catid,
        categoryName: categoryNameById[catid] || "Uncategorized",
        entries: grouped[catid],
        }));
    }, [filteredEntries, categoryNameById, categories]);

  // =====================================================
  // 🚗 Entry Display Helper
  // =====================================================
  const getEntryTitle = (entry) => {
    const yearModel = [entry.year, entry.model].filter(Boolean).join(" ");
    return yearModel || entry.description || "Unnamed Entry";
  };

  // =====================================================
  // 🧭 Navigate to Existing Vote Page
  // =====================================================
    const goToVotePage = (entry) => {
    if (!eventslug) {
        logger.error("❌ Manual vote missing event slug:", {
        eventId,
        eventDoc,
        entryId: entry.id,
        });

        showPageStatus(
        "error",
        "Unable to open voting page because the event slug is missing."
        );

        return;
    }

    logger.log("🗳️ Manual vote selected:", {
        eventId,
        eventslug,
        entryId: entry.id,
        catid: entry.catid,
    });

    setSessionValue("voteEntrySource", "manual");

    trackEvent({
        action: GA_EVENTS.MANUAL_VOTE_ENTRY_SELECTED,
        parameters: {
            event_slug: eventslug,
            event_id: eventId,
            entry_id: entry.id,
            category_id: entry.catid,
            voting_type: votingType,
            source_page: window.location.pathname,
        },
    });
        
    removeSessionValue("qrVoteTab");
    navigate(`/${eventslug}/vote/${entry.id}`);
    };

    const manualVoteSeo = {
        ...buildEventPageSeo({
            eventDoc,
            pageKey: "manualVote",
            fallbackTitle: `Manual Voting | ${
            eventDoc?.name || "Event"
            } | Let's Go Vote`,
            fallbackDescription:
            "Browse event entries and submit votes without scanning QR codes.",
        }),

        path: `/${eventslug}/manual_vote`,

        breadcrumbs: [
            { name: "Home", path: "/" },
            {
            name: eventDoc?.name || "Event",
            path: `/${eventslug}/event`,
            },
            {
            name: "Manual Voting",
            path: `/${eventslug}/manual_vote`,
            },
        ],
    };

  // =====================================================
  // ⏳ Loading States
  // =====================================================
  if (eventLoading || !ready || loadingPage) {
    return (
      <div className="container mt-4">
        <h2>Manual Voting</h2>
        <div className="alert alert-info">Loading manual voting options...</div>
      </div>
    );
  }

  // =====================================================
  // 🧱 Render Page
  // =====================================================
return (
    <>
    <SeoHead {...manualVoteSeo} />
    <section id="top-content" className="container-fluid py-5">
        <div className="row mb-4">
        <div className="col text-start">
            <h1 className="display-5">Manual Voting</h1>
            <p className="lead text-muted">
            Browse entries by category and select an item to vote on.
            </p>
        </div>
        </div>

        <div className="white-section p-4 rounded shadow-sm bg-white">

        {status && (
            <div
            className={`alert ${
                status.type === "success"
                ? "alert-success"
                : status.type === "error"
                ? "alert-danger"
                : status.type === "warning"
                ? "alert-warning"
                : "alert-info"
            }`}
            >
            {status.message}
            </div>
        )}

        <div className="alert alert-info mb-4">
            <strong>Manual Voting:</strong>{" "}
            Select an entry below. This uses the same voting page as scanning a QR code.
        </div>

        <div className="d-flex align-items-center gap-3 flex-wrap mb-4">
            <label>Filter by Category:</label>

            <select
            className="form-select w-auto"
            value={selectedCatId}
            onChange={(e) => setSelectedCatId(e.target.value)}
            >
            <option value="">All Categories</option>

            {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                {cat.name || cat.category || "Unnamed Category"}
                </option>
            ))}
            </select>

            {selectedCatId && (
            <button
                className="btn btn-outline-secondary"
                onClick={() => setSelectedCatId("")}
            >
                Reset
            </button>
            )}

            <input
            type="text"
            className="form-control"
            style={{ maxWidth: "380px" }}
            placeholder="Search number, owner, year, model..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            />

            <button
            className="btn btn-outline-secondary ms-auto"
            onClick={() => navigate(`/${eventslug}/ballot`)}
            disabled={!eventslug}
            >
            Back to My Ballot
            </button>
        </div>

        {filteredEntries.length === 0 ? (
            <div className="alert alert-warning">
            No entries found for the selected filters.
            </div>
        ) : (
            entriesGroupedByCategory.map((group) => (
            <div key={group.catid} className="mb-4">
                <h4 className="mb-2">{group.categoryName}</h4>

                <div className="list-group">
                {group.entries.map((entry) => {

                    const existingVoteForEntry = userVotes.find(
                    (vote) => String(vote.cid) === String(entry.id)
                    );

                    const existingVoteForCategory = userVotes.find(
                    (vote) => String(vote.catid) === String(entry.catid)
                    );

                    const rankedChoiceRank = existingVoteForCategory?.rankings?.find(
                    (ranking) => String(ranking.cid) === String(entry.id)
                    );

                    const alreadyVotedForThisEntry =
                    isSelectionVoting &&
                    votingType !== "ranked_choice" &&
                    existingVoteForEntry &&
                    String(existingVoteForEntry.cid) === String(entry.id);

                    const isRankedChoiceSelected =
                    votingType === "ranked_choice" && rankedChoiceRank;

                    const buttonText = isJudgedVoting
                    ? existingVoteForEntry
                        ? "Update Scorecard"
                        : "Score Entry"
                    : votingType === "ranked_choice"
                    ? existingVoteForCategory
                        ? "Update Rankings"
                        : "Rank Entries"
                    : alreadyVotedForThisEntry
                    ? "View Vote"
                    : existingVoteForCategory
                    ? "Update Vote"
                    : "Vote";

                    const buttonClass = isJudgedVoting
                    ? existingVoteForEntry
                        ? "btn-success"
                        : "btn-primary"
                    : isRankedChoiceSelected || alreadyVotedForThisEntry
                    ? "btn-success"
                    : "btn-primary";

                    const entryStatusText = isJudgedVoting
                    ? existingVoteForEntry
                        ? "Scorecard submitted"
                        : "Not scored yet"
                    : votingType === "ranked_choice"
                    ? rankedChoiceRank
                        ? `Rank #${rankedChoiceRank.rank}`
                        : existingVoteForCategory
                        ? "Not ranked"
                        : "No rankings yet"
                    : alreadyVotedForThisEntry
                    ? "Current vote"
                    : existingVoteForCategory
                    ? "Category already voted"
                    : "Not voted yet";

                    const entryStatusClass = isJudgedVoting
                    ? existingVoteForEntry
                        ? "text-success"
                        : "text-muted"
                    : votingType === "ranked_choice"
                    ? rankedChoiceRank
                        ? "text-success"
                        : existingVoteForCategory
                        ? "text-warning"
                        : "text-muted"
                    : alreadyVotedForThisEntry
                    ? "text-success"
                    : existingVoteForCategory
                    ? "text-warning"
                    : "text-muted";

                    return (
                    <div key={entry.id} className="list-group-item">
                        <div className="row leftAlign align-items-center">

                        <div className="col-sm-1">

                            <span
                            className={`badge rounded-pill ${
                                rankedChoiceRank || existingVoteForEntry ? "bg-success" : "bg-primary"
                            }`}
                            >
                            #{entry.regcnt || "N/A"}
                            </span>

                            {rankedChoiceRank && (
                            <div className="small text-success mt-1">
                                Rank #{rankedChoiceRank.rank}
                            </div>
                            )}

                        </div>

                        <div className="col-sm-4">
                            <strong>{getEntryTitle(entry)}</strong>
                            {entry.description && (
                            <div className="small text-muted">
                                {entry.description}
                            </div>
                            )}
                        </div>

                        <div className="col-sm-3">
                            {entry.owner ||
                            [entry.ownerFirst, entry.ownerLast].filter(Boolean).join(" ") ||
                            "Unknown Owner"}
                        </div>

                        <div className="col-sm-2">
                        <span className={`small ${entryStatusClass}`}>
                            {entryStatusText}
                        </span>
                        </div>

                        <div className="col-sm-2 text-end">

                            <button
                            className={`btn btn-sm ${buttonClass}`}
                            onClick={() => goToVotePage(entry)}
                            >
                            {buttonText}
                            </button>
                        </div>

                        </div>
                    </div>
                    );
                })}
                </div>
            </div>
            ))
        )}
        </div>
    </section>
    </>
    );
}

export default ManualVote;